import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useMenuStore } from '@/stores/menu'
import { resolveMenuChildPath, type MenuItem } from '@/layouts/config'

export interface BreadcrumbItem {
  path?: string
  title: string
}

type MenuChild = NonNullable<MenuItem['children']>[number]

/** 与侧栏、菜单 store 一致：解析子菜单实际路由 path */
function resolvedChildPath(child: MenuChild): string {
  return resolveMenuChildPath(child.key, child.path)
}

/** 子路由 path 是否属于某子菜单（如 …/list 与 …/details/:id） */
function pathBelongsToChild(routePath: string, child: MenuChild): boolean {
  const path = resolvedChildPath(child)
  if (!path) return false
  if (routePath === path) return true
  const base = path.replace(/\/list\/?$/, '')
  if (base === path) return false
  return routePath.startsWith(`${base}/details`)
}

function findMenuMatch(
  routePath: string,
  menus: MenuItem[],
): { parent: MenuItem; child: MenuChild } | null {
  for (const menu of menus) {
    if (!menu.children?.length) continue
    for (const child of menu.children) {
      if (pathBelongsToChild(routePath, child)) {
        return { parent: menu, child }
      }
    }
  }
  return null
}

/** 接口子项 key/path 异常时，按路由 name 兜底关联父菜单 */
const ROUTE_MENU_FALLBACKS: Array<{
  routeNames: readonly string[]
  matchChild: (child: MenuChild) => boolean
}> = [
  {
    routeNames: ['emergency-case-list', 'emergency-case-details'],
    matchChild: (c) => c.key === 'case' || c.label === '案件管理',
  },
  {
    routeNames: ['emergency-evaluation-list'],
    matchChild: (c) => c.key === 'evaluation' || c.label === '评价管理',
  },
  {
    routeNames: ['service-ai-assistant-list'],
    matchChild: (c) => c.key === 'ai-assistant' || c.label === 'AI助手',
  },
]

function findRouteMenuFallback(
  routeName: string | symbol | undefined | null,
  menus: MenuItem[],
): { parent: MenuItem; child: MenuChild } | null {
  if (routeName == null || typeof routeName !== 'string') return null
  const fallback = ROUTE_MENU_FALLBACKS.find((item) => item.routeNames.includes(routeName))
  if (!fallback) return null
  for (const menu of menus) {
    if (!menu.children?.length) continue
    const child = menu.children.find(fallback.matchChild)
    if (child) return { parent: menu, child }
  }
  return null
}

/** 根据当前路由与侧边栏菜单生成面包屑：仅「首页 + 一级菜单名称 + 页面名称」 */
export function useBreadcrumb() {
  const route = useRoute()
  const menuStore = useMenuStore()

  const items = computed<BreadcrumbItem[]>(() => {
    const result: BreadcrumbItem[] = [{ path: '/', title: '首页' }]
    const path = route.path
    const metaTitle = (route.meta?.title as string) ?? ''

    if (path === '/') {
      return result
    }

    let nested = findMenuMatch(path, menuStore.menus)
    if (!nested) {
      nested = findRouteMenuFallback(route.name, menuStore.menus)
    }
    if (nested) {
      result.push({ title: nested.parent.label })
      const listPath = resolvedChildPath(nested.child)
      if (listPath && path === listPath) {
        result.push({ title: nested.child.label })
      } else {
        result.push({ title: metaTitle || nested.child.label })
      }
      return result
    }

    for (const menu of menuStore.menus) {
      if (menu.children?.length) continue
      if (menu.path === path) {
        result.push({ title: menu.label })
        return result
      }
    }

    if (metaTitle) {
      result.push({ title: metaTitle })
    }
    return result
  })

  return { items }
}
